import React from "react";
import clsx from "clsx";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  perPage,
  onPageChange,
}) => {
  const handlePageClick = (page: number) => {
    if (page > 0 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    const maxPagesToShow = 5;
    const halfMaxPages = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      if (currentPage > halfMaxPages + 2) {
        pageNumbers.push('start-ellipsis');
      }

      const startPage = Math.max(2, currentPage - halfMaxPages);
      const endPage = Math.min(totalPages - 1, currentPage + halfMaxPages);

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (currentPage < totalPages - halfMaxPages - 1) {
        pageNumbers.push('end-ellipsis');
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <div className="flex items-center justify-between ps-3 overflow-x-auto">
      <div className="text-gray-400 text-sm">
        {`Showing ${(currentPage-1)*perPage} to ${Math.min(currentPage*perPage, totalCount)} of ${totalCount} entries`}
      </div>
      <div className="flex justify-center items-center py-4">
        <button
          className={clsx("px-3 py-1 mx-1 border rounded text-sm", {
            "cursor-not-allowed opacity-50": currentPage === 1,
            "hover:bg-gray-200 dark:hover:bg-gray-500 border border-border": currentPage !== 1,
          })}
          onClick={() => handlePageClick(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {getPageNumbers().map((page, index) => {
          if (typeof page === "string") {
            return (
              <span key={`${page}-${index}`} className="px-3 py-1 mx-1">
                ...
              </span>
            );
          } else {
            return (
              <button
                key={page}
                className={clsx("px-2 py-1 mx-1 text-sm", {
                  "bg-primary text-black": page === currentPage,
                })}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            );
          }
        })}
        <button
          className={clsx("px-3 py-1 mx-1 border rounded text-sm", {
            "cursor-not-allowed opacity-50": currentPage === totalPages,
            "hover:bg-gray-200 dark:hover:bg-gray-500 border border-border": currentPage !== totalPages,
          })}
          onClick={() => handlePageClick(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;